/**
 * ShipmentDetail — centrale Zendingdetail-pagina.
 *
 * 6 tabs: Overzicht · Colli · Documenten · Klant · Consolidatie · Activiteit
 *
 * Acties in de header-bar:
 *  - Label printen           (alle rollen met view-rechten)
 *  - Toevoegen aan consolidatie (Manager+)
 *  - Factuur maken           (Manager+) — opent CreateInvoiceFromShipmentDialog
 *  - Terug naar overzicht
 *
 * Stubs voor Documenten/Klant tab — schema is voorbereid in backend
 * (`shipping_labels.documents`, `customer_id`) maar UI-vulling komt in latere
 * sprint.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Printer, Layers, FileText, Loader2, AlertCircle, ChevronRight,
  Package as PackageIcon, User as UserIcon, FileBarChart, Activity, Receipt,
  Box, MapPin, CheckCircle2, Circle, ExternalLink, Plus, Edit2, Send,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

import ServiceTypeBadge from "./ServiceTypeBadge";
import DestinationBadge from "./DestinationBadge";
import CreateInvoiceFromShipmentDialog from "./CreateInvoiceFromShipmentDialog";
import EditShipmentForm from "./EditShipmentForm";
import ShipmentDocumentsTab from "./ShipmentDocumentsTab";
import WarehouseReceiptDialog from "./WarehouseReceiptDialog";
import FinancialReleaseCard from "./FinancialReleaseCard";
import ShipmentStatusPlanningTab from "./ShipmentStatusPlanningTab";
import AddPieceDialog from "./AddPieceDialog";
import ShipmentLifecycleActions from "./ShipmentLifecycleActions";
import TrackingIdentityCard from "./TrackingIdentityCard";
import { ShipmentPrealertLink } from "../operational/ShipmentPrealertLink";
import { CentralAgentReceipt } from "../operational/CentralAgentReceipt";

const PACKAGING_LABELS = {
  box: "Doos", barrel: "Barrel", suitcase: "Koffer", pallet: "Pallet",
  bag: "Zak", loose: "Los item", other: "Anders",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

// P0 SPRINT (Aug 2026) — Canonical NL tab-key → interne state key mapping
// voor SMART SHIPMENT NAVIGATION. Hoisted buiten de component zodat
// useCallback/useEffect deps stabiel blijven.
const TAB_URL_TO_INTERNAL = {
  overzicht: "overview", colli: "colli", documenten: "documents",
  klant: "customer", consolidatie: "consolidation",
  statusplanning: "planning", activiteit: "activity",
};
const TAB_INTERNAL_TO_URL = Object.fromEntries(
  Object.entries(TAB_URL_TO_INTERNAL).map(([k, v]) => [v, k])
);
function _readTabFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const raw = (p.get("tab") || "").toLowerCase();
    return TAB_URL_TO_INTERNAL[raw] || "overview";
  } catch { return "overview"; }
}

export default function ShipmentDetail({ wNumber, onBack }) {
  const { hasPermission, user } = useAuth();
  const { runTask } = useGlobalLoading();
  const isMedewerker = (user?.role || "").toLowerCase() === "medewerker";

  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [warehouseReceiptOpen, setWarehouseReceiptOpen] = useState(false);
  const [addPieceOpen, setAddPieceOpen] = useState(false);
  const [consolidation, setConsolidation] = useState(null);

  // Bidirectional URL ↔ tab sync (SMART SHIPMENT NAVIGATION)
  const [activeTab, setActiveTabRaw] = useState(_readTabFromUrl());
  const setActiveTab = useCallback((next) => {
    setActiveTabRaw(next);
    try {
      const url = new URL(window.location.href);
      const urlKey = TAB_INTERNAL_TO_URL[next] || "overzicht";
      if (urlKey === "overzicht") url.searchParams.delete("tab");
      else url.searchParams.set("tab", urlKey);
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
  }, []);
  // React op browser back/forward
  useEffect(() => {
    const onPop = () => setActiveTabRaw(_readTabFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // Als wNumber wijzigt (nieuwe shipment) → sync tab vanuit URL opnieuw
  useEffect(() => { setActiveTabRaw(_readTabFromUrl()); }, [wNumber]);

  const triggerReload = () => setReloadTick((n) => n + 1);

  // P0.19 SPRINT — CANONICAL PRINT ACTIONS via GlobalLoadingProvider.
  // Warehouse Receipt en Shipping Label printen altijd door de PDF in
  // een nieuw tabblad te openen (blob-URL) + auto-print. runTask
  // dedupliceert dubbele klikken en toont fullscreen loading overlay.
  const printWarehouseReceipt = useCallback(async () => {
    if (!label?.id) return;
    try {
      await runTask({
        id: `wr-print-${label.id}`,
        message: "Warehouse Receipt wordt gegenereerd…",
        successMessage: "Warehouse Receipt geopend",
        run: async () => {
          const resp = await adminApi.get(
            `/admin/shipping-labels/${label.id}/warehouse-receipt/pdf?print_action=true`,
            { responseType: "blob" }
          );
          const blobUrl = window.URL.createObjectURL(
            new Blob([resp.data], { type: "application/pdf" })
          );
          const w = window.open(blobUrl, "_blank", "noopener");
          if (w) w.onload = () => setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 300);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Warehouse Receipt genereren mislukt");
      }
    }
  }, [label?.id, runTask]);

  const printShippingLabel = useCallback(async () => {
    if (!label?.id) return;
    try {
      await runTask({
        id: `sl-print-${label.id}`,
        message: "Shipping Label wordt gegenereerd…",
        successMessage: "Shipping Label geopend",
        run: async () => {
          const resp = await adminApi.get(
            `/admin/shipping-labels/${label.id}/pdf`,
            { responseType: "blob" }
          );
          const blobUrl = window.URL.createObjectURL(
            new Blob([resp.data], { type: "application/pdf" })
          );
          const w = window.open(blobUrl, "_blank", "noopener");
          if (w) w.onload = () => setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 300);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Shipping Label genereren mislukt");
      }
    }
  }, [label?.id, runTask]);

  // P0 SPRINT — luister naar het "open Warehouse Receipt" event vanuit
  // de Documenten-tab's SystemDocumentsSection. Zo houden we de bestaande
  // WarehouseReceiptDialog als single source-of-truth voor previews.
  useEffect(() => {
    const handler = (e) => {
      if (!e?.detail?.shipmentId) return;
      if (e.detail.shipmentId === label?.id) {
        setWarehouseReceiptOpen(true);
      }
    };
    window.addEventListener("shipment:open-warehouse-receipt", handler);
    return () => window.removeEventListener("shipment:open-warehouse-receipt", handler);
  }, [label?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // P0.19 SPRINT — CANONICAL SHIPMENT LOOKUP.
      // Gebruik de nieuwe filter-agnostische endpoint zodat een W-nummer
      // ALTIJD gevonden wordt, ook als het shipment gearchiveerd, verzonden,
      // aangekomen of gefilterd is in de lijstweergave.
      const r = await adminApi.get(
        `/admin/shipments/by-wnumber/${encodeURIComponent(wNumber)}`
      );
      const doc = r.data;
      if (!doc?.id) {
        setError(`Zending met W-nummer ${wNumber} niet gevonden.`);
        setLabel(null);
        return;
      }
      setLabel(doc);

      // Probeer consolidatie-link op te halen — best-effort
      try {
        const consResp = await adminApi.get("/admin/consolidations", {
          params: { q: doc.tracking_number, limit: 5 },
        });
        const matchCons = (consResp.data.items || []).find((c) => {
          // Niet 100% precies (we hebben hier niet de exacte link-info), maar
          // good-enough voor de simple weergave. Echte check zou via
          // /consolidations/{id}/shipments verlopen.
          return true;
        });
        setConsolidation(matchCons || null);
      } catch { /* niet kritiek */ }
    } catch (err) {
      setError(formatApiError(err) || "Laden zending mislukt");
    } finally {
      setLoading(false);
    }
  }, [wNumber]);

  useEffect(() => { load(); }, [load, reloadTick]);

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Laden zendingdetail…
      </div>
    );
  }
  if (error || !label) {
    return (
      <div className="space-y-3">
        <Button onClick={onBack} variant="outline" className="rounded-none text-xs" data-testid="shipment-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug naar overzicht
        </Button>
        <div className="bg-red-50 border border-red-200 p-6 rounded-md">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-chivo font-bold text-red-900">Zending niet gevonden</h2>
              <p className="text-sm text-red-800 mt-1">{error || "Onbekende fout"}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sd = label.shipment_details || {};
  const items = sd.items || [];
  const totalCbm = items.length > 0
    ? items.reduce((s, it) => s + (Number(it.cbm) || 0), 0)
    : ((sd.length_cm || 0) * (sd.width_cm || 0) * (sd.height_cm || 0) * (sd.piece_count || 1)) / 1_000_000;

  const linkedInvoice = label.linked_invoice_number ? {
    id: label.linked_invoice_id,
    number: label.linked_invoice_number,
  } : null;

  return (
    <div className="space-y-4" data-testid="shipment-detail">
      <Button onClick={onBack} variant="outline" className="rounded-none text-xs" data-testid="shipment-detail-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Terug naar overzicht
      </Button>

      {/* Header */}
      <ShipmentPrealertLink shipmentId={label.id} />
      <CentralAgentReceipt shipment={label} onReceived={async () => triggerReload()} />
      <div className="bg-gradient-to-r from-[#082c50] to-[#0a3a6b] text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PackageIcon className="w-6 h-6 text-yellow-400" />
          <div>
            <div className="text-xs text-blue-100 uppercase tracking-wide">Zending</div>
            <div className="font-mono font-black text-2xl tracking-wider" data-testid="shipment-detail-wnumber">
              {label.tracking_number}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ServiceTypeBadge serviceType={label.service_type} size="lg" testId="shipment-detail-service-badge" />
          <DestinationBadge code={label.destination_country_code}
                            name={label.destination_country_name}
                            size="lg" testId="shipment-detail-destination-badge" />
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wide bg-emerald-500/20 text-emerald-50 border border-emerald-300/40"
                data-testid="shipment-detail-status">
            <CheckCircle2 className="w-3.5 h-3.5" /> In magazijn
          </span>
          {/* P0 SPRINT — Permanent visibility badges */}
          {label.archived_at && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wide bg-amber-500/20 text-amber-50 border border-amber-300/50"
                  data-testid="shipment-detail-badge-archived">
              Gearchiveerd
            </span>
          )}
          {label.is_deleted && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wide bg-red-500/20 text-red-50 border border-red-300/50"
                  data-testid="shipment-detail-badge-deleted">
              Verwijderd
            </span>
          )}
          {(label.release_status || "").toLowerCase() === "hold" && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wide bg-red-500/30 text-red-50 border border-red-300/60"
                  data-testid="shipment-detail-badge-hold">
              HOLD
            </span>
          )}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-gray-200 px-4 py-2">
        {hasPermission("quotes:edit") && (
          <Button size="sm"
                  onClick={() => setEditOpen(true)}
                  className="rounded-none text-xs bg-[#0055FF] text-white hover:bg-[#0044CC]"
                  data-testid="shipment-detail-action-edit">
            <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Zending bewerken
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={printShippingLabel}
                className="rounded-none text-xs" data-testid="shipment-detail-action-print-label">
          <Printer className="w-3.5 h-3.5 mr-1.5" /> Shipping Label printen
        </Button>
        <Button size="sm"
                onClick={printWarehouseReceipt}
                className="rounded-none text-xs bg-amber-500 text-white hover:bg-amber-600"
                data-testid="shipment-detail-action-print-warehouse-receipt">
          <Printer className="w-3.5 h-3.5 mr-1.5" /> Warehouse Receipt printen
        </Button>
        <Button size="sm" variant="outline"
                onClick={() => setWarehouseReceiptOpen(true)}
                className="rounded-none text-xs"
                data-testid="shipment-detail-action-warehouse-receipt">
          <FileText className="w-3.5 h-3.5 mr-1.5" /> Warehouse Receipt bekijken
        </Button>
        {hasPermission("quotes:edit") && (
          <Button size="sm" variant="outline"
                  onClick={() => {
                    setActiveTab("documents");
                    toast("Kies onder in het documenten-overzicht 'Verstuur document' om te versturen. Koppel eerst een document als de lijst leeg is.", { duration: 4000 });
                  }}
                  className="rounded-none text-xs bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-900"
                  data-testid="shipment-detail-action-send-document">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Verstuur document
          </Button>
        )}
        {!isMedewerker && (
          <Button size="sm" variant="outline"
                  onClick={() => toast.info("Toevoegen aan consolidatie kan vanuit het magazijn-overzicht of de consolidatie-detailpagina.")}
                  className="rounded-none text-xs"
                  data-testid="shipment-detail-action-add-consolidation">
            <Layers className="w-3.5 h-3.5 mr-1.5" /> Toevoegen aan consolidatie
          </Button>
        )}
        {!isMedewerker && !linkedInvoice && (
          <Button size="sm"
                  onClick={() => setInvoiceOpen(true)}
                  className="rounded-none text-xs bg-[#0055FF] text-white hover:bg-[#0044CC]"
                  data-testid="shipment-detail-action-create-invoice">
            <Receipt className="w-3.5 h-3.5 mr-1.5" /> Factuur maken
          </Button>
        )}
        {linkedInvoice && (
          <a href={`/admin?section=facturen`} target="_self"
             className="inline-flex items-center gap-1 px-3 py-1.5 border border-blue-200 bg-blue-50 text-blue-900 text-xs hover:bg-blue-100"
             data-testid="shipment-detail-linked-invoice">
            <Receipt className="w-3.5 h-3.5" />
            <span className="font-bold">{linkedInvoice.number}</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {/* P0 SPRINT — lifecycle acties (archive/soft-delete/restore) */}
        <div className="ml-auto flex items-center gap-2">
          <ShipmentLifecycleActions shipment={label} onChanged={triggerReload} />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white border border-gray-200">
        <TabsList className="flex flex-wrap justify-start max-w-full border-b border-gray-200 rounded-none bg-gray-50 p-0 h-auto">
          <TabsTrigger value="overview" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-overview">Overzicht</TabsTrigger>
          <TabsTrigger value="colli" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-colli">Colli ({items.length || 1})</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-documents">Documenten</TabsTrigger>
          <TabsTrigger value="customer" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-customer">Klant</TabsTrigger>
          <TabsTrigger value="consolidation" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-consolidation">Consolidatie</TabsTrigger>
          <TabsTrigger value="planning" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-planning">Statusplanning</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#0055FF]"
                       data-testid="shipment-tab-activity">Activiteit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="p-5">
          <div className="mb-4">
            <FinancialReleaseCard
              label={label}
              onChanged={(newLabel) => {
                if (newLabel) setLabel(newLabel);
                setReloadTick((t) => t + 1);
              }}
            />
          </div>
          <div className="mb-4">
            <TrackingIdentityCard
              label={label}
              onChanged={(updated) => {
                if (updated && typeof updated === "object") setLabel(updated);
                setReloadTick((t) => t + 1);
              }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard title="Afzender" icon={UserIcon}>
              <PartyDisplay party={label.sender} />
            </InfoCard>
            <InfoCard title="Ontvanger" icon={MapPin}>
              <PartyDisplay party={label.recipient} />
            </InfoCard>
            <InfoCard title="Zending-info" icon={PackageIcon}>
              <KV label="Magazijn / hub" value={label.warehouse_hub} />
              <KV label="Agentschap" value={label.agency} />
              <KV label="Extern W-nummer" value={label.linked_external_tracking_number}
                  mono />
              <KV label="Aangemaakt" value={`${fmt(label.created_at)} · ${label.created_by_email || ""}`} />
              <KV label="Status" value="In magazijn" />
            </InfoCard>
            <InfoCard title="Totalen" icon={Box}>
              <KV label="Totaal colli" value={String(sd.piece_count ?? items.length ?? 0)} />
              <KV label="Totaal gewicht" value={`${(sd.weight_kg ?? 0).toFixed?.(2) || sd.weight_kg} kg`} />
              <KV label="Totaal CBM" value={`${totalCbm.toFixed(4)} m³`} mono />
              <KV label="Aangegeven waarde" value={`${sd.currency || "EUR"} ${(sd.value ?? 0).toFixed?.(2) || sd.value || 0}`} />
              <KV label="Bestemming" value={label.destination_country_name || sd.destination || "—"} />
            </InfoCard>
            {(sd.customer_note || sd.internal_note) && (
              <InfoCard title="Notities" icon={FileText} className="md:col-span-2">
                {sd.customer_note && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase font-bold tracking-wide text-gray-500">Klantnotitie</div>
                    <div className="text-sm text-[#082c50]">{sd.customer_note}</div>
                  </div>
                )}
                {sd.internal_note && (
                  <div>
                    <div className="text-[10px] uppercase font-bold tracking-wide text-gray-500">Interne notitie</div>
                    <div className="text-sm text-[#082c50]">{sd.internal_note}</div>
                  </div>
                )}
              </InfoCard>
            )}
          </div>
        </TabsContent>

        <TabsContent value="colli" className="p-5" data-testid="shipment-detail-colli-tab">
          {/* P0 SPRINT — Colli toevoegen (extra doos, zelfde W-nummer) */}
          {hasPermission("quotes:edit") && (
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-gray-500">
                W-nummer <span className="font-mono font-bold text-[#0A192F]">{label.tracking_number}</span> blijft ongewijzigd — extra colli wordt aan deze zending gekoppeld.
              </div>
              <Button size="sm" onClick={() => setAddPieceOpen(true)}
                      className="rounded-none text-xs bg-[#0055FF] text-white hover:bg-[#0044CC]"
                      data-testid="shipment-detail-add-piece-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Colli toevoegen
              </Button>
            </div>
          )}
          {items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-600 tracking-wide">
                  <tr>
                    <th className="text-left p-3">Colli</th>
                    <th className="text-left p-3">Omschrijving</th>
                    <th className="text-left p-3">Verpakking</th>
                    <th className="text-right p-3">Aantal</th>
                    <th className="text-right p-3">Gewicht</th>
                    <th className="text-right p-3">Afmetingen</th>
                    <th className="text-right p-3">CBM</th>
                    <th className="text-right p-3">Waarde</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={it.id || idx} className="border-t border-gray-100">
                      <td className="p-3 font-bold text-[#082c50]">#{it.colli_number || idx + 1}</td>
                      <td className="p-3 text-xs">{it.description || "—"}</td>
                      <td className="p-3 text-xs">{PACKAGING_LABELS[it.packaging_type] || it.packaging_type || "—"}</td>
                      <td className="p-3 text-right font-mono text-xs">{it.quantity || 1}</td>
                      <td className="p-3 text-right font-mono text-xs">{(it.weight_kg || 0).toFixed?.(2) || it.weight_kg || 0} kg</td>
                      <td className="p-3 text-right font-mono text-xs text-gray-500">
                        {(it.length_cm || 0)}×{(it.width_cm || 0)}×{(it.height_cm || 0)} cm
                      </td>
                      <td className="p-3 text-right font-mono text-xs">{(it.cbm || 0).toFixed?.(4) || it.cbm} m³</td>
                      <td className="p-3 text-right font-mono text-xs">{sd.currency || "EUR"} {(it.declared_value || 0).toFixed?.(2) || it.declared_value || 0}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200 text-xs font-bold">
                  <tr>
                    <td colSpan={3} className="p-3 text-right">Totaal:</td>
                    <td className="p-3 text-right font-mono">{sd.piece_count ?? items.length}</td>
                    <td className="p-3 text-right font-mono">{(sd.weight_kg || 0).toFixed?.(2) || sd.weight_kg} kg</td>
                    <td />
                    <td className="p-3 text-right font-mono">{totalCbm.toFixed(4)} m³</td>
                    <td className="p-3 text-right font-mono">{sd.currency || "EUR"} {(sd.value || 0).toFixed?.(2) || sd.value || 0}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-md text-sm text-amber-900">
              <div className="font-bold">Geen colli-regels (legacy zending)</div>
              <div className="text-xs mt-1">
                Deze zending is aangemaakt vóór de multi-colli upgrade. Globale waarden:
                {' '}{sd.piece_count ?? 0} colli · {sd.weight_kg ?? 0} kg ·
                {' '}{sd.length_cm || 0}×{sd.width_cm || 0}×{sd.height_cm || 0} cm.
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="p-5">
          <ShipmentDocumentsTab shipment={label} />
        </TabsContent>

        <TabsContent value="customer" className="p-5">
          {label.customer_id ? (
            <CustomerLinkedDisplay customerId={label.customer_id} />
          ) : (
            <div className="bg-gray-50 border border-dashed border-gray-300 p-8 rounded-md text-center"
                 data-testid="shipment-tab-customer-stub">
              <UserIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <h3 className="font-chivo font-bold text-[#082c50]">Geen gekoppelde klant</h3>
              <p className="text-sm text-gray-600 mt-1">
                Bij deze zending is geen bestaande klant gekoppeld. Bij registratie kun je
                via &quot;Zoek bestaande klant&quot; een klant koppelen, of via bewerken later toevoegen.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="consolidation" className="p-5">
          {consolidation ? (
            <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-md"
                 data-testid="shipment-tab-consolidation-info">
              <div className="text-[10px] uppercase font-bold tracking-wide text-indigo-700">Gekoppelde consolidatie</div>
              <div className="font-mono font-black text-lg text-indigo-900 mt-1">{consolidation.consolidation_number}</div>
              <div className="text-sm text-indigo-800 mt-1">
                {consolidation.departure_hub_label} <ChevronRight className="inline w-3 h-3" /> {consolidation.destination_hub_label}
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-md text-sm text-amber-900"
                 data-testid="shipment-tab-consolidation-empty">
              <div className="font-bold flex items-center gap-2"><Circle className="w-3.5 h-3.5" /> Niet geconsolideerd</div>
              <div className="text-xs mt-1">
                Voeg deze zending toe aan een open consolidatie vanuit het magazijn-overzicht of via &quot;Zendingen toevoegen&quot; op de consolidatie-detailpagina.
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="planning" className="p-5">
          <ShipmentStatusPlanningTab
            shipmentId={label.id}
            label={label}
            onReload={() => setReloadTick((t) => t + 1)}
          />
        </TabsContent>

        <TabsContent value="activity" className="p-5">
          <ShipmentActivityList labelId={label.id} />
        </TabsContent>
      </Tabs>

      {invoiceOpen && (
        <CreateInvoiceFromShipmentDialog
          shipment={label}
          onClose={() => setInvoiceOpen(false)}
          onCreated={(inv) => {
            setInvoiceOpen(false);
            triggerReload();
            toast.success(`Factuur ${inv.invoice_number} aangemaakt`);
          }}
        />
      )}
      {editOpen && (
        <EditShipmentForm
          open={editOpen}
          shipment={label}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            triggerReload();
          }}
        />
      )}
      <WarehouseReceiptDialog
        open={warehouseReceiptOpen}
        onOpenChange={setWarehouseReceiptOpen}
        shipment={label}
      />
      {/* P0 SPRINT — Extra colli toevoegen aan bestaande zending */}
      {addPieceOpen && (
        <AddPieceDialog
          open={addPieceOpen}
          onClose={() => setAddPieceOpen(false)}
          shipment={label}
          onAdded={(updated) => {
            setLabel(updated);
            triggerReload();
            toast.success("Colli toegevoegd — totalen bijgewerkt");
          }}
        />
      )}
    </div>
  );
}

function InfoCard({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 p-4 rounded-md ${className}`}>
      <div className="flex items-center gap-2 mb-2 text-[#082c50]">
        <Icon className="w-4 h-4" />
        <h3 className="font-chivo font-bold text-sm">{title}</h3>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function KV({ label, value, mono = false }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className={`text-[#082c50] text-right ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function PartyDisplay({ party }) {
  if (!party) return <div className="text-xs text-gray-400">—</div>;
  const addrParts = [
    [party.street, party.house_number, party.house_number_addition].filter(Boolean).join(" ").trim(),
    [party.postal_code, party.city].filter(Boolean).join(" ").trim(),
    party.country,
  ].filter(Boolean);
  return (
    <div className="space-y-0.5 text-xs">
      <div className="font-bold text-[#082c50]">{party.name || party.company || "—"}</div>
      {party.company && party.name && <div className="text-gray-600">{party.company}</div>}
      {(party.phone || party.whatsapp) && (
        <div className="text-gray-600 font-mono">{party.phone || party.whatsapp}</div>
      )}
      {party.email && <div className="text-gray-600">{party.email}</div>}
      {addrParts.length > 0 && (
        <div className="text-gray-500 pt-1">
          {addrParts.map((p, i) => <div key={i}>{p}</div>)}
        </div>
      )}
    </div>
  );
}

function CustomerLinkedDisplay({ customerId }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get(`/admin/customers/${customerId}`);
        if (!cancelled) setCustomer(r.data.customer || r.data);
      } catch {
        setCustomer(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);
  if (loading) return <div className="text-xs text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Laden klant…</div>;
  if (!customer) return <div className="text-xs text-amber-700">Klant kon niet worden geladen.</div>;
  return (
    <div className="bg-blue-50 border border-blue-200 p-4 rounded-md" data-testid="shipment-tab-customer-linked">
      <div className="text-[10px] uppercase font-bold tracking-wide text-blue-700">Gekoppelde klant</div>
      <div className="font-chivo font-bold text-lg text-blue-900 mt-0.5">{customer.display_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || customer.company_name || customer.email}</div>
      <div className="text-xs text-blue-800 mt-1 space-y-0.5">
        {customer.email && <div>{customer.email}</div>}
        {customer.phone && <div className="font-mono">{customer.phone}</div>}
        {customer.city && <div>{customer.city} {customer.country ? `· ${customer.country}` : ""}</div>}
      </div>
    </div>
  );
}

function ShipmentActivityList({ labelId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/audit-logs", { params: { limit: 200 } });
        const all = Array.isArray(r.data) ? r.data : (r.data.items || []);
        if (!cancelled) {
          setItems(all.filter((a) => a.target_id === labelId));
        }
      } catch { /* niet kritiek */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [labelId]);
  if (loading) return <div className="text-xs text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Laden activiteit…</div>;
  if (items.length === 0) return <div className="text-xs text-gray-500">Geen activiteit geregistreerd voor deze zending.</div>;

  const ACTION_LABELS = {
    document_linked:     { label: "Document gekoppeld",    color: "border-emerald-400" },
    document_uploaded:   { label: "Document geüpload",     color: "border-blue-400" },
    document_replaced:   { label: "Document vervangen",    color: "border-amber-400" },
    document_unlinked:   { label: "Document ontkoppeld",   color: "border-slate-400" },
    document_downloaded: { label: "Document gedownload",   color: "border-indigo-400" },
    document_viewed:     { label: "Document bekeken",      color: "border-indigo-300" },
    document_sent:       { label: "Document verzonden",    color: "border-purple-500" },
    document_auto_linked: { label: "Auto-gekoppeld",       color: "border-emerald-500" },
  };

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="shipment-tab-activity-list">
      {items.map((a, idx) => {
        const meta = ACTION_LABELS[a.action] || { label: a.action, color: "border-blue-300" };
        const md = a.metadata || {};
        return (
          <div key={idx} className={`border-l-2 pl-3 py-1 ${meta.color}`}
               data-testid={`activity-row-${a.id || idx}`}>
            <div className="text-xs font-bold text-[#082c50]">{meta.label}</div>
            <div className="text-[10px] text-gray-500 flex flex-wrap gap-x-2">
              <span>{fmt(a.created_at)}</span>
              <span>·</span>
              <span>{a.actor_email || "systeem"}</span>
              {md.doc_type && <span>· type: {md.doc_type}</span>}
              {md.filename && <span>· {md.filename}</span>}
              {md.to && Array.isArray(md.to) && <span>· naar: {md.to.join(", ")}</span>}
              {md.subject && <span className="italic">· {md.subject.slice(0, 40)}{md.subject.length > 40 ? "…" : ""}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
